import { useEffect } from 'react';
import type { UseFormWatch } from 'react-hook-form';
import type { UseFormSetValue } from 'react-hook-form/dist/types/form';

import type { IFormValues } from '../index';

export const useCheckboxWatcher = ({
    watch,
    setValue,
}: {
    watch: UseFormWatch<IFormValues>;
    setValue: UseFormSetValue<IFormValues>;
}) => {
    useEffect(() => {
        const subscription = watch((value, { name }) => {
            if (!name?.startsWith('checkedIds.')) {
                return;
            }

            const [, otherId, id] = name.split('.');
            const checkedIds = value?.checkedIds?.[Number(otherId)] ?? {};
            const childItems = Object.entries(checkedIds).filter(([key]) => key !== 'parent');

            const allChildItemsChecked = childItems.every(([, checkboxValue]) => checkboxValue);
            const someChildItemsChecked = childItems.some(([, checkboxValue]) => checkboxValue);
            const parentState = checkedIds?.parent;

            if (id !== 'parent') {
                if (allChildItemsChecked) {
                    setValue(`checkedIds.${otherId}.parent`, true);
                } else if (someChildItemsChecked) {
                    setValue(`checkedIds.${otherId}.parent`, 'intermediate');
                } else {
                    setValue(`checkedIds.${otherId}.parent`, false);
                }
            } else if (
                parentState !== 'intermediate' &&
                ((parentState && !allChildItemsChecked) || (!parentState && someChildItemsChecked))
            ) {
                const newChildState = childItems.reduce((acc: Record<string, boolean | undefined>, [childId]) => {
                    acc[childId] = parentState;

                    return acc;
                }, {});

                setValue(`checkedIds.${otherId}`, { parent: parentState, ...newChildState });
                childItems.forEach(([childId]) => setValue(`checkedIds.${otherId}.${childId}`, parentState));
            }
        });

        return () => subscription.unsubscribe();
    }, [setValue, watch]);
};
